/** Экран серверных проектов: создание, просмотр и переход в планировщик. */
import { useForm } from "@tanstack/react-form";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { apiHooks, ProjectSharingMode, ProjectSharingPermission } from "@/api";
import { routerPaths } from "@/app/routerPaths";
import { hasAccessToken } from "@/shared/store";
import { UIKit } from "@/shared/ui";

const projectSchema = z.object({
	description: z.string(),
	name: z.string().min(1, "Введите название проекта"),
});

type Project = {
	id: string;
	name: string;
	description: string | null;
	lastModifiedDate: string;
	isOwner: boolean;
	sharingMode: ProjectSharingMode;
	sharingPermission: ProjectSharingPermission;
	sharedWithEmail: string | null;
};

const sharingSchema = z
	.object({
		email: z.string().trim(),
		sharingMode: z.nativeEnum(ProjectSharingMode),
		sharingPermission: z.nativeEnum(ProjectSharingPermission),
	})
	.superRefine((values, context) => {
		if (
			values.sharingMode === ProjectSharingMode.Email &&
			!z.string().email().safeParse(values.email).success
		) {
			context.addIssue({ code: "custom", message: "Укажите корректную почту", path: ["email"] });
		}
	});

export const ProjectsPage = () => {
	const navigate = useNavigate();
	const projectsApi = apiHooks.projects.useListProjects();
	const createProjectApi = apiHooks.projects.useCreateProject();
	const deleteProjectApi = apiHooks.projects.useDeleteProject();
	const [editingProject, setEditingProject] = useState<Project | null>(null);
	const [sharingProject, setSharingProject] = useState<Project | null>(null);
	const [deletingProject, setDeletingProject] = useState<Project | null>(null);
	const [deleteError, setDeleteError] = useState(false);
	const createForm = useForm({
		defaultValues: { description: "", name: "" },
		validators: { onChange: projectSchema },
		onSubmit: async ({ value }) => {
			const response = await createProjectApi.execute({
				...value,
				description: value.description || null,
			});
			if (response.data !== null) {
				await navigate(`${routerPaths.planner}?projectId=${response.data.id}`);
			}
		},
	});

	if (!hasAccessToken()) {
		return (
			<div className="flex flex-1 items-center justify-center p-6">
				<div className="w-full max-w-sm border border-border bg-surface p-6">
					<UIKit.Text size="xl">Мои проекты</UIKit.Text>
					<UIKit.Text className="mt-2 text-ink">
						Войдите, чтобы хранить проекты на сервере.
					</UIKit.Text>
					<Link
						className="mt-6 inline-block rounded-lg bg-accent px-4 py-2 text-sm text-white"
						to={routerPaths.login}
					>
						Войти
					</Link>
				</div>
			</div>
		);
	}

	return (
		<div className="flex-1 overflow-y-auto bg-bg px-5 py-8">
			<div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-editor">
				<div className="h-fit border border-border bg-surface p-5">
					<UIKit.Text size="xl">Новый проект</UIKit.Text>
					<form className="mt-5 space-y-4" action={() => createForm.handleSubmit()}>
						<UIKit.FormField form={createForm} name="name">
							{(field) => (
								<UIKit.TextInput
									label="Название"
									value={field.state.value}
									onChange={field.handleChange}
									autoComplete="off"
								/>
							)}
						</UIKit.FormField>
						<UIKit.FormField form={createForm} name="description">
							{(field) => (
								<UIKit.TextInput
									label="Описание"
									value={field.state.value}
									onChange={field.handleChange}
									autoComplete="off"
								/>
							)}
						</UIKit.FormField>
						<UIKit.Button
							className="w-full border-accent bg-accent py-2.5 text-sm text-white"
							type="submit"
							disabled={createProjectApi.isPending}
						>
							{createProjectApi.isPending ? "Создаём..." : "Создать проект"}
						</UIKit.Button>
					</form>
				</div>

				<div>
					<div className="mb-4 flex items-center justify-between">
						<UIKit.Text size="xl">Мои проекты</UIKit.Text>
						<UIKit.Button onClick={() => projectsApi.execute()} disabled={projectsApi.isPending}>
							Обновить
						</UIKit.Button>
					</div>
					{projectsApi.isPending && (
						<UIKit.Text className="text-ink">Загружаем проекты...</UIKit.Text>
					)}
					{projectsApi.data?.length === 0 && (
						<UIKit.Text className="text-ink">Пока нет ни одного проекта.</UIKit.Text>
					)}
					<div className="grid gap-3">
						{projectsApi.data?.map((project) => (
							<ProjectCard
								key={project.id}
								project={project}
								onEdit={() => setEditingProject(project)}
								onShare={() => setSharingProject(project)}
								onDelete={() => {
									setDeleteError(false);
									setDeletingProject(project);
								}}
							/>
						))}
					</div>
				</div>
			</div>
			{editingProject !== null && (
				<EditProjectModal
					project={editingProject}
					onClose={() => setEditingProject(null)}
					onUpdated={async () => {
						await projectsApi.execute();
						setEditingProject(null);
					}}
				/>
			)}
			{sharingProject !== null && (
				<ProjectSharingModal
					project={sharingProject}
					onClose={() => setSharingProject(null)}
					onUpdated={async () => {
						await projectsApi.execute();
						setSharingProject(null);
					}}
				/>
			)}
			{deletingProject !== null && (
				<UIKit.ConfirmModal
					title="Удалить проект?"
					description={`Проект «${deletingProject.name}» будет удалён без возможности восстановления.`}
					onClose={() => {
						if (!deleteProjectApi.isPending) setDeletingProject(null);
					}}
					actions={
						<>
							<UIKit.Button
								disabled={deleteProjectApi.isPending}
								onClick={() => setDeletingProject(null)}
							>
								Отмена
							</UIKit.Button>
							<UIKit.Button
								className="border-red-700 bg-red-700 text-white hover:border-red-600 hover:bg-red-600"
								disabled={deleteProjectApi.isPending}
								onClick={async () => {
									setDeleteError(false);
									try {
										const response = await deleteProjectApi.execute({ id: deletingProject.id });
										if (response.error !== null) {
											setDeleteError(true);
											return;
										}
										setDeletingProject(null);
										await projectsApi.execute();
									} catch {
										setDeleteError(true);
									}
								}}
							>
								{deleteProjectApi.isPending ? "Удаляем..." : "Удалить"}
							</UIKit.Button>
						</>
					}
				>
					{deleteError && (
						<UIKit.Text className="mt-3 text-danger">Не удалось удалить проект.</UIKit.Text>
					)}
				</UIKit.ConfirmModal>
			)}
		</div>
	);
};

type ProjectCardProps = {
	project: Project;
	onEdit: () => void;
	onShare: () => void;
	onDelete: () => void;
};

const ProjectCard = ({ project, onEdit, onShare, onDelete }: ProjectCardProps) => (
	<div className="border border-border bg-surface p-5">
		<div className="flex flex-wrap items-start justify-between gap-4">
			<div>
				<UIKit.Text size="large">{project.name}</UIKit.Text>
				{project.description && (
					<UIKit.Text className="mt-1 text-ink">{project.description}</UIKit.Text>
				)}
				<UIKit.Text size="small" className="mt-3 text-ink">
					Изменён {new Date(project.lastModifiedDate).toLocaleDateString("ru-RU")}
				</UIKit.Text>
			</div>
			<div className="flex flex-wrap gap-2">
				{project.isOwner ? (
					<>
						<UIKit.Button onClick={onEdit}>Редактировать</UIKit.Button>
						<UIKit.Button onClick={onShare}>Поделиться</UIKit.Button>
						<UIKit.Button onClick={onDelete}>Удалить</UIKit.Button>
					</>
				) : (
					<UIKit.Text size="small" className="self-center text-ink">
						{project.sharingPermission === ProjectSharingPermission.Edit
							? "Доступ с редактированием"
							: "Доступ для просмотра"}
					</UIKit.Text>
				)}
				<Link
					className="rounded-lg bg-accent px-3 py-2 text-sm text-white"
					to={`${routerPaths.planner}?projectId=${project.id}`}
				>
					{project.isOwner || project.sharingPermission === ProjectSharingPermission.Edit
						? "Открыть планировщик"
						: "Смотреть"}
				</Link>
			</div>
		</div>
	</div>
);

type ProjectSharingModalProps = {
	project: Project;
	onClose: () => void;
	onUpdated: () => Promise<void>;
};

const ProjectSharingModal = ({ project, onClose, onUpdated }: ProjectSharingModalProps) => {
	const updateSharing = apiHooks.projects.useUpdateProjectSharing();
	const [copied, setCopied] = useState(false);
	const shareUrl = `${window.location.origin}${routerPaths.planner}?projectId=${project.id}`;
	const form = useForm({
		defaultValues: {
			email: project.sharedWithEmail ?? "",
			sharingMode: project.sharingMode,
			sharingPermission: project.sharingPermission,
		},
		validators: { onChange: sharingSchema },
		onSubmit: async ({ value }) => {
			const response = await updateSharing.execute(
				{ id: project.id },
				{
					email: value.sharingMode === ProjectSharingMode.Email ? value.email : null,
					sharingMode: value.sharingMode,
					sharingPermission:
						value.sharingMode === ProjectSharingMode.Email
							? value.sharingPermission
							: ProjectSharingPermission.View,
				},
			);
			if (response.error === null) await onUpdated();
		},
	});

	return (
		<UIKit.Modal
			ariaLabel="Настройка доступа к проекту"
			className="p-5 md:max-w-md"
			onClose={onClose}
		>
			<UIKit.Text size="xl">Поделиться проектом</UIKit.Text>
			<form className="mt-5 space-y-4" action={() => form.handleSubmit()}>
				<UIKit.FormField form={form} name="sharingMode">
					{(field) => (
						<UIKit.SegmentedControl
							className="max-w-full"
							value={field.state.value}
							options={[
								{ label: "Запрещено", value: ProjectSharingMode.Disabled },
								{ label: "По ссылке", value: ProjectSharingMode.Link },
								{ label: "По почте", value: ProjectSharingMode.Email },
							]}
							onChange={field.handleChange}
						/>
					)}
				</UIKit.FormField>
				<form.Subscribe selector={(state) => state.values.sharingMode}>
					{(sharingMode) =>
						sharingMode === ProjectSharingMode.Email ? (
							<>
								<UIKit.FormField form={form} name="sharingPermission">
									{(field) => (
										<UIKit.SegmentedControl
											className="max-w-full"
											value={field.state.value}
											options={[
												{ label: "Только просмотр", value: ProjectSharingPermission.View },
												{ label: "Редактирование", value: ProjectSharingPermission.Edit },
											]}
											onChange={field.handleChange}
										/>
									)}
								</UIKit.FormField>
								<UIKit.FormField form={form} name="email">
									{(field) => (
										<UIKit.TextInput
											label="Почта аккаунта"
											name={field.name}
											value={field.state.value}
											onChange={field.handleChange}
											type="email"
											autoComplete="email"
										/>
									)}
								</UIKit.FormField>
							</>
						) : sharingMode === ProjectSharingMode.Link ? (
							<div className="flex gap-2">
								<UIKit.TextInput label="Ссылка" value={shareUrl} onChange={() => {}} readOnly />
								<UIKit.Button
									className="self-end"
									type="button"
									onClick={() => {
										void navigator.clipboard.writeText(shareUrl).then(() => setCopied(true));
									}}
								>
									{copied ? "Скопировано" : "Копировать"}
								</UIKit.Button>
							</div>
						) : null
					}
				</form.Subscribe>
				<div className="flex justify-end gap-2">
					<UIKit.Button onClick={onClose}>Отмена</UIKit.Button>
					<UIKit.Button
						className="border-accent bg-accent text-white"
						type="submit"
						disabled={updateSharing.isPending}
					>
						{updateSharing.isPending ? "Сохраняем..." : "Сохранить"}
					</UIKit.Button>
				</div>
			</form>
		</UIKit.Modal>
	);
};

type EditProjectModalProps = {
	project: Project;
	onClose: () => void;
	onUpdated: () => Promise<void>;
};

const EditProjectModal = ({ project, onClose, onUpdated }: EditProjectModalProps) => {
	const updateProjectApi = apiHooks.projects.useUpdateProject();
	const form = useForm({
		defaultValues: { description: project.description ?? "", name: project.name },
		validators: { onChange: projectSchema },
		onSubmit: async ({ value }) => {
			const response = await updateProjectApi.execute(
				{ id: project.id },
				{ ...value, description: value.description || null },
			);
			if (response.error !== null) return;
			await onUpdated();
		},
	});

	return (
		<UIKit.Modal ariaLabel="Редактирование проекта" className="p-5 md:max-w-md" onClose={onClose}>
			<UIKit.Text size="xl">Редактировать проект</UIKit.Text>
			<form className="mt-5 space-y-4" action={() => form.handleSubmit()}>
				<UIKit.FormField form={form} name="name">
					{(field) => (
						<UIKit.TextInput
							label="Название"
							value={field.state.value}
							onChange={field.handleChange}
						/>
					)}
				</UIKit.FormField>
				<UIKit.FormField form={form} name="description">
					{(field) => (
						<UIKit.TextInput
							label="Описание"
							value={field.state.value}
							onChange={field.handleChange}
						/>
					)}
				</UIKit.FormField>
				<div className="flex justify-end gap-2">
					<UIKit.Button onClick={onClose}>Отмена</UIKit.Button>
					<UIKit.Button
						className="border-accent bg-accent text-white"
						type="submit"
						disabled={updateProjectApi.isPending}
					>
						{updateProjectApi.isPending ? "Сохраняем..." : "Сохранить"}
					</UIKit.Button>
				</div>
			</form>
		</UIKit.Modal>
	);
};
